import React, { useCallback, useRef, useState } from 'react';

import { MAX_FILESTACK_FILE_SIZE } from '@aline/common';
import { CircularProgress, IconButton } from '@mui/material';
import { FileText, Upload, X } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import { useVersionAwareUpload } from 'src/helpers/docx-upload-target';
import { getVersionAwareUploadFailureMessage } from 'src/helpers/docx-upload-target/get-version-aware-upload-failure-message';
import { acceptedMimeTypePrefixes, acceptedMimeTypes } from 'src/helpers/integrations/filestack';
import { makeStyles } from 'tss-react/mui';

/** A document reference produced by uploading a file to answer an askUser question. */
export interface AskUserFile {
  documentId: string;
  name: string;
}

const useStyles = makeStyles()(theme => ({
  dropzone: {
    // The dropzone itself takes focus (its file input is visually hidden), so it has to show it.
    '&:focus': { borderColor: theme.appColors.primary },
    '&:hover': { borderColor: theme.appColors.primary },
    alignItems: 'center',
    background: theme.palette.common.white,
    border: `1px dashed ${theme.appColors.rule ?? '#c8d0d4'}`,
    borderRadius: 8,
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 12,
    gap: theme.spacing(0.5),
    justifyContent: 'center',
    outline: 'none',
    padding: theme.spacing(3),
    textAlign: 'center',
    width: '100%',
  },
  dropzoneActive: { borderColor: theme.appColors.primary },
  dropzoneDisabled: {
    '&:hover': { borderColor: theme.appColors.rule },
    borderColor: theme.appColors.rule,
    cursor: 'default',
    opacity: 0.6,
  },
  fileIcon: { color: theme.appColors.secondaryType, flexShrink: 0 },
  fileName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileRow: {
    alignItems: 'center',
    background: theme.palette.action.hover,
    borderRadius: 8,
    color: theme.appColors.iconsType,
    display: 'flex',
    fontSize: 12,
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
  },
  removeButton: { color: theme.appColors.iconsType, flexShrink: 0, padding: theme.spacing(0.5) },
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1), width: '100%' },
}));

function getIsFileAccepted(file: File): boolean {
  if (acceptedMimeTypes.has(file.type)) return true;
  return acceptedMimeTypePrefixes.some(prefix => file.type.startsWith(prefix));
}

/**
 * Split dropped files into the ones to upload and a skipped count: drop anything
 * over the size cap or with an unsupported MIME type, then cap what remains to
 * the field's free slots. Pure, so the size / MIME / maxFiles rules are unit-
 * testable without a dropzone.
 */
export function selectFilesToUpload(
  dropped: File[],
  { currentCount, maxFiles }: { currentCount: number; maxFiles?: number | null }
): { skipped: number; toUpload: File[] } {
  const accepted = dropped.filter(
    file => file.size <= MAX_FILESTACK_FILE_SIZE && getIsFileAccepted(file)
  );
  const skipped = dropped.length - accepted.length;
  const remaining = typeof maxFiles === 'number' ? Math.max(0, maxFiles - currentCount) : undefined;
  const toUpload = remaining === undefined ? accepted : accepted.slice(0, remaining);
  return { skipped, toUpload };
}

/**
 * Upload control for an askUser file question. Dropped files are uploaded to
 * storage and turned into Aline Documents (via createFiles); their
 * `{ documentId, name }` references become the answer. Removal is local-only
 * (drops the reference from the draft) — the answer has not been submitted yet,
 * so there is no workflow document to detach.
 */
export function AskUserFileUpload({
  disabled,
  files,
  maxFiles,
  onChange,
  onUploadingChange,
}: {
  files: AskUserFile[];
  onChange: (files: AskUserFile[]) => void;
  disabled?: boolean;
  maxFiles?: number | null;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const { classes, cx } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const { uploadFiles, versionDialog } = useVersionAwareUpload();
  /** Synchronous reentrancy guard: `uploading` state lags a render. */
  const uploadingRef = useRef(false);
  /**
   * The latest `files`, so the merge after an in-flight upload reflects any
   * files the user removed while it was uploading (the `onDrop` closure would
   * otherwise merge into a stale list and resurrect the removed reference).
   */
  const filesRef = useRef(files);
  filesRef.current = files;
  const atMax = typeof maxFiles === 'number' && files.length >= maxFiles;

  const onDrop = useCallback(
    async (dropped: File[]) => {
      if (disabled || uploadingRef.current || dropped.length === 0) return;

      const { skipped, toUpload } = selectFilesToUpload(dropped, {
        currentCount: filesRef.current.length,
        maxFiles,
      });
      if (skipped > 0)
        enqueueSnackbar(
          `${skipped} file${skipped > 1 ? 's were' : ' was'} skipped (unsupported format or too large).`,
          { variant: 'warning' }
        );
      if (toUpload.length === 0) return;

      uploadingRef.current = true;
      setUploading(true);
      onUploadingChange?.(true);
      try {
        const { failedFiles, uploadedFiles } = await uploadFiles(toUpload);
        const failureMessage = getVersionAwareUploadFailureMessage({
          failedCount: failedFiles.length,
          uploadedCount: uploadedFiles.length,
        });
        if (failureMessage) enqueueSnackbar(failureMessage, { variant: 'error' });

        const uploaded: AskUserFile[] = uploadedFiles.map(({ documentId, name }) => ({
          documentId,
          name,
        }));
        const merged = [...filesRef.current, ...uploaded];
        onChange(typeof maxFiles === 'number' ? merged.slice(0, maxFiles) : merged);
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'File upload failed.', {
          variant: 'error',
        });
      } finally {
        uploadingRef.current = false;
        setUploading(false);
        onUploadingChange?.(false);
      }
    },
    [disabled, enqueueSnackbar, maxFiles, onChange, onUploadingChange, uploadFiles]
  );

  const isDropzoneDisabled = Boolean(disabled) || atMax || uploading;
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    disabled: isDropzoneDisabled,
    onDrop,
  });

  const removeFile = (index: number) => onChange(files.filter((_, i) => i !== index));

  return (
    <div className={classes.root}>
      {versionDialog}
      {atMax ? null : (
        <div
          {...getRootProps({
            className: cx(
              classes.dropzone,
              isDragActive && classes.dropzoneActive,
              isDropzoneDisabled && classes.dropzoneDisabled
            ),
          })}
          data-field-focus-target
        >
          <input {...getInputProps()} />
          {uploading ? <CircularProgress size={16} /> : <Upload size={16} />}
          <span>{uploading ? 'Uploading…' : 'Drop a file here, or click to upload'}</span>
        </div>
      )}
      {files.map((file, index) => (
        <div key={`${file.documentId}:${index}`} className={classes.fileRow}>
          <FileText className={classes.fileIcon} size={14} />
          <span className={classes.fileName}>{file.name}</span>
          <IconButton
            className={classes.removeButton}
            disabled={disabled}
            size="small"
            onClick={() => removeFile(index)}
          >
            <X size={14} />
          </IconButton>
        </div>
      ))}
    </div>
  );
}
